"use client";

/**
 * Belt-and-suspenders `document.title` sync.
 *
 * Next.js's `generateMetadata` sets the `<title>` tag at the
 * HTML level on the initial response, which is 99% of the job.
 * But two edge cases leave the title stale:
 *
 *   1. **BFCache restores** — mobile Chrome / Safari preserve
 *      a paused tab's full DOM state (including `<title>`)
 *      when the user backs-and-forwards through history. A new
 *      server response is never fetched, so the cached title
 *      wins.
 *
 *   2. **Soft client-side navigations** — for future routes
 *      that navigate within the survey shell, React doesn't
 *      re-request the HTML, so generateMetadata doesn't run
 *      again. Page-level `title` changes without this sync
 *      would be dropped.
 *
 * This component runs a tiny effect that overwrites
 * `document.title` after mount. Covers both cases by
 * brute-forcing the tab label to match whatever the server
 * computed. Renders nothing — pure side effect.
 */

import { useEffect } from "react";

interface Props {
  title: string;
}

export default function SyncDocumentTitle({ title }: Props) {
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (document.title === title) return;
    document.title = title;
  }, [title]);
  return null;
}
